import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, AlertTriangle, Clock, XCircle } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusBadge = {
  expired: "bg-red-600 text-white",
  critical: "bg-red-100 text-red-700 border-red-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  expiring_soon: "bg-yellow-100 text-yellow-700 border-yellow-200",
  ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
  unknown: "bg-slate-100 text-slate-500",
};

const Documents = () => {
  const { data: docs, isLoading } = useSWR("/documents", fetcher);
  const { data: reminders } = useSWR("/documents/reminders/summary?days=60", fetcher);
  const { data: types } = useSWR("/documents/types", fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: vehicles } = useSWR("/vehicles", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    owner_type: "driver", owner_id: "", type: "driving_licence",
    issue_date: "", expiry_date: "", notes: "",
  });

  const allowedTypes = form.owner_type === "driver" ? (types?.driver || []) : (types?.vehicle || []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_id) return toast.error("Owner required");
    try {
      await api.post("/documents", form);
      toast.success("Document saved");
      setOpen(false);
      setForm({ owner_type: "driver", owner_id: "", type: "driving_licence",
                issue_date: "", expiry_date: "", notes: "" });
      mutate("/documents");
      mutate("/documents/reminders/summary?days=60");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const ownerName = (d) => {
    if (d.owner_type === "driver") {
      const drv = (drivers || []).find((x) => x.id === d.owner_id);
      return drv ? `${drv.full_name} (${drv.driver_code})` : d.owner_id;
    }
    const v = (vehicles || []).find((x) => x.id === d.owner_id);
    return v ? v.registration : d.owner_id;
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Compliance</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Documents</h1>
          <p className="text-sm text-slate-500 mt-1">Driver + vehicle documents with expiry alerts.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-red hover:bg-brand-red-hover" data-testid="doc-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add document
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New document</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">For</Label>
                  <Select value={form.owner_type} onValueChange={(v) => setForm((f) => ({ ...f, owner_type: v, owner_id: "", type: v === "driver" ? "driving_licence" : "mot" }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">
                  {form.owner_type === "driver" ? "Driver" : "Vehicle"}
                </Label>
                  <Select value={form.owner_id} onValueChange={(v) => setForm((f) => ({ ...f, owner_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {form.owner_type === "driver"
                        ? (drivers || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>)
                        : (vehicles || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.registration}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedTypes.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Issue date</Label>
                  <Input type="date" value={form.issue_date}
                         onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Expiry date</Label>
                  <Input type="date" required value={form.expiry_date} data-testid="doc-expiry"
                         onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} /></div>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="doc-save" className="bg-brand-red hover:bg-brand-red-hover">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reminders — bucketed by urgency (expired / critical / warning / expiring soon) */}
      {reminders && reminders.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reminders-summary">
          {[
            { key: "expired",       label: "Expired",       colour: "border-red-300 bg-red-50 text-red-900",       iconClr: "text-red-600",     icon: XCircle },
            { key: "critical",      label: "Critical ≤ 7d", colour: "border-red-200 bg-red-50 text-red-800",       iconClr: "text-red-500",     icon: AlertTriangle },
            { key: "warning",       label: "Warning ≤ 14d", colour: "border-amber-300 bg-amber-50 text-amber-900", iconClr: "text-amber-600",   icon: AlertTriangle },
            { key: "expiring_soon", label: "Soon ≤ 30d",    colour: "border-yellow-300 bg-yellow-50 text-yellow-900", iconClr: "text-yellow-600", icon: Clock },
          ].map(({ key, label, colour, iconClr, icon: Icon }) => {
            const count = reminders.counts?.[key] || 0;
            const items = reminders.items?.[key] || [];
            return (
              <Card key={key}
                    className={`p-3 rounded-2xl border ${colour}`}
                    data-testid={`reminder-bucket-${key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-4 h-4 ${iconClr}`} />
                    <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
                  </div>
                  <span className="font-heading text-2xl font-bold leading-none">{count}</span>
                </div>
                {items.length > 0 && (
                  <ul className="mt-2.5 space-y-1 max-h-32 overflow-y-auto text-[11px]">
                    {items.slice(0, 4).map((it) => (
                      <li key={it.id} className="flex items-center justify-between gap-2 py-1 border-t border-current/10 pt-1.5"
                          data-testid={`reminder-item-${it.id}`}>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{it.doc_label}</div>
                          <div className="truncate opacity-75 text-[10px]">
                            {it.owner_label}
                            {it.owner_sublabel ? ` · ${it.owner_sublabel}` : ""}
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap font-mono">
                          {ddmmyyyy(it.expiry_date)}
                          <div className="text-[9px] uppercase tracking-wider opacity-75">
                            {it.days_until_expiry < 0
                              ? `${Math.abs(it.days_until_expiry)}d ago`
                              : `${it.days_until_expiry}d`}
                          </div>
                        </div>
                      </li>
                    ))}
                    {items.length > 4 && (
                      <li className="text-[10px] italic opacity-70 pt-1">
                        +{items.length - 4} more · see table below
                      </li>
                    )}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Issued</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-center px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(docs || []).map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{d.owner_type}</div>
                    <div className="font-medium">{ownerName(d)}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{d.type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{ddmmyyyy(d.issue_date)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{ddmmyyyy(d.expiry_date)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusBadge[d.expiry_status] || ""}`}>
                      {d.expiry_status.replace("_", " ")}
                    </Badge>
                  </td>
                </tr>
              ))}
              {!isLoading && (docs || []).length === 0 && (
                <tr><td colSpan="5" className="text-center py-12 text-slate-500 text-sm">
                  <ShieldCheck className="w-6 h-6 mx-auto mb-2 opacity-40" />No documents.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Documents;
